import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { 
  Download, 
  Settings, 
  Play, 
  Pause, 
  CheckCircle, 
  AlertCircle,
  X,
  Monitor,
  Smartphone,
  Film,
  Clock,
  FileVideo,
  Loader2
} from 'lucide-react';
import videoEncoder from '../services/videoEncoder';
import { projectAPI } from '../services/api';

const ExportPanel = ({ 
  timeline, 
  projectId, 
  onClose, 
  className = '' 
}) => {
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('idle'); // idle, exporting, completed, failed
  const [exportError, setExportError] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const [currentExportId, setCurrentExportId] = useState(null);

  // Export settings
  const [exportSettings, setExportSettings] = useState({
    format: 'mp4_h264',
    quality: 'high',
    aspectRatio: timeline?.aspectRatio || '16:9',
    framerate: 30,
    includeAudio: true,
    fileName: `export_${Date.now()}`
  });

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [estimatedSize, setEstimatedSize] = useState(null);

  // Available export formats
  const exportFormats = [
    {
      id: 'mp4_h264',
      name: 'MP4 (H.264)',
      description: 'Standard MP4 format, widely compatible',
      extension: 'mp4',
      recommended: true
    },
    {
      id: 'mp4_h265',
      name: 'MP4 (H.265)',
      description: 'Smaller file size, newer format',
      extension: 'mp4',
      recommended: false
    },
    {
      id: 'mov_h264',
      name: 'QuickTime MOV',
      description: 'Apple QuickTime format',
      extension: 'mov',
      recommended: false
    },
    {
      id: 'webm_vp9',
      name: 'WebM (VP9)',
      description: 'Open web video format',
      extension: 'webm',
      recommended: false
    }
  ];

  // Quality presets
  const qualityPresets = [
    {
      id: 'low',
      name: 'Low Quality',
      description: '720p, smaller file size',
      resolution: '1280x720',
      bitrate: '1 Mbps'
    },
    {
      id: 'medium',
      name: 'Medium Quality',
      description: '1080p, balanced quality',
      resolution: '1920x1080',
      bitrate: '3 Mbps'
    },
    {
      id: 'high',
      name: 'High Quality',
      description: '1080p, high bitrate',
      resolution: '1920x1080',
      bitrate: '5 Mbps'
    },
    {
      id: 'ultra',
      name: 'Ultra Quality',
      description: '4K, maximum quality',
      resolution: '3840x2160',
      bitrate: '15 Mbps'
    }
  ];

  // Platform-specific presets
  const platformPresets = [
    {
      id: 'youtube',
      name: 'YouTube',
      icon: Monitor,
      description: '1080p optimized for YouTube',
      resolution: '1920x1080',
      aspectRatio: '16:9',
      quality: 'high',
      format: 'mp4',
      maxDuration: 900 // 15 minutes
    },
    {
      id: 'youtube_shorts',
      name: 'YouTube Shorts',
      icon: Smartphone,
      description: 'Vertical 1080p for YouTube Shorts',
      resolution: '1080x1920',
      aspectRatio: '9:16',
      quality: 'high',
      format: 'mp4',
      maxDuration: 60
    },
    {
      id: 'instagram_reel',
      name: 'Instagram Reel',
      icon: Smartphone,
      description: 'Vertical video for Instagram Reels',
      resolution: '1080x1920',
      aspectRatio: '9:16',
      quality: 'high',
      format: 'mp4',
      maxDuration: 90
    },
    {
      id: 'instagram_post',
      name: 'Instagram Post',
      icon: Film,
      description: 'Square video for Instagram feed',
      resolution: '1080x1080',
      aspectRatio: '1:1',
      quality: 'medium',
      format: 'mp4',
      maxDuration: 60
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: Smartphone,
      description: 'Vertical video optimized for TikTok',
      resolution: '1080x1920',
      aspectRatio: '9:16',
      quality: 'high',
      format: 'mp4',
      maxDuration: 600
    },
    {
      id: 'twitter',
      name: 'Twitter',
      icon: Monitor,
      description: 'Landscape video for Twitter',
      resolution: '1280x720',
      aspectRatio: '16:9',
      quality: 'medium',
      format: 'mp4',
      maxDuration: 140
    }
  ];

  // Calculate estimates when settings change
  useEffect(() => {
    if (timeline?.duration) {
      const estimatedSeconds = videoEncoder.estimateExportTime(timeline.duration, exportSettings.quality);
      setEstimatedTime(estimatedSeconds);

      // Rough file size estimation (bitrate * duration)
      const bitrateMap = { low: 1, medium: 3, high: 5, ultra: 15 };
      const bitrateMbps = bitrateMap[exportSettings.quality] || 5;
      const estimatedMB = (bitrateMbps * timeline.duration) / 8; // Convert bits to bytes
      setEstimatedSize(estimatedMB);
    }
  }, [timeline?.duration, exportSettings.quality]);

  // Poll export progress
  useEffect(() => {
    let progressInterval;
    
    if (isExporting && currentExportId) {
      progressInterval = setInterval(async () => {
        try {
          const progress = videoEncoder.getExportProgress(currentExportId);
          if (progress) {
            setExportProgress(progress.progress || 0);
            
            if (progress.status === 'completed') {
              setIsExporting(false);
              setExportStatus('completed');
              clearInterval(progressInterval);
            } else if (progress.status === 'failed') {
              setIsExporting(false);
              setExportStatus('failed');
              setExportError(progress.error || 'Export failed');
              clearInterval(progressInterval);
            }
          }
        } catch (error) {
          console.error('Failed to get export progress:', error);
        }
      }, 1000);
    }

    return () => {
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [isExporting, currentExportId]);

  const handleExport = useCallback(async () => {
    if (!timeline || isExporting) return;

    try {
      setIsExporting(true);
      setExportStatus('exporting');
      setExportProgress(0);
      setExportError(null);

      // Choose export method based on browser support
      let result;
      if (videoEncoder.isSupported) {
        // Use WebCodecs for modern browsers
        result = await videoEncoder.exportTimeline(timeline, {
          format: exportSettings.format.split('_')[0], // mp4, mov, webm
          quality: exportSettings.quality,
          aspectRatio: exportSettings.aspectRatio,
          framerate: exportSettings.framerate
        });
      } else {
        // Fallback to backend export
        result = await exportWithBackend();
      }

      setExportResult(result);
      setExportStatus('completed');
      setIsExporting(false);

    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error.message);
      setExportStatus('failed');
      setIsExporting(false);
    }
  }, [timeline, exportSettings, isExporting]);

  const exportWithBackend = async () => {
    try {
      // Start backend export using new timeline API
      const exportResponse = await projectAPI.exportTimeline(projectId, {
        clips: timeline.clips,
        duration: timeline.duration,
        aspectRatio: timeline.aspectRatio
      }, exportSettings);

      const exportId = exportResponse.export_id;
      setCurrentExportId(exportId);

      // Poll for completion
      return new Promise((resolve, reject) => {
        const pollInterval = setInterval(async () => {
          try {
            const progress = await projectAPI.getExportProgress(projectId, exportId);
            setExportProgress(progress.progress_percent || 0);

            if (progress.status === 'completed') {
              clearInterval(pollInterval);
              
              // Download the file
              const blob = await projectAPI.downloadExport(projectId, exportId);
              resolve({
                blob,
                duration: timeline.duration,
                format: exportSettings.format,
                size: blob.size
              });
            } else if (progress.status === 'failed') {
              clearInterval(pollInterval);
              reject(new Error(progress.error_message || 'Export failed'));
            }
          } catch (error) {
            clearInterval(pollInterval);
            reject(error);
          }
        }, 2000);
      });

    } catch (error) {
      throw new Error(`Backend export failed: ${error.message}`);
    }
  };

  const handleDownload = useCallback(() => {
    if (!exportResult?.blob) return;

    const format = exportFormats.find(f => f.id === exportSettings.format);
    const fileName = `${exportSettings.fileName}.${format?.extension || 'mp4'}`;
    
    const url = URL.createObjectURL(exportResult.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [exportResult, exportSettings]);

  const handleCancel = useCallback(() => {
    if (currentExportId) {
      videoEncoder.cancelExport(currentExportId);
    }
    setIsExporting(false);
    setExportStatus('idle');
    setExportProgress(0);
    setCurrentExportId(null);
  }, [currentExportId]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
    } else {
      return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
    }
  };

  return (
    <Card className={`w-full max-w-2xl bg-zinc-900 border-zinc-700 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <FileVideo className="w-5 h-5 text-blue-400" />
          <h2 className="text-xl font-semibold text-white">Export Video</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="text-gray-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-6 space-y-6">
        {/* Export Status */}
        {exportStatus === 'exporting' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Exporting video...</span>
              <span className="text-sm text-gray-400">{Math.round(exportProgress)}%</span>
            </div>
            <Progress value={exportProgress} className="w-full" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {estimatedTime && `Est. ${formatTime(estimatedTime * (1 - exportProgress / 100))}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Export Success */}
        {exportStatus === 'completed' && exportResult && (
          <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-medium">Export completed!</span>
            </div>
            <div className="space-y-2 text-sm text-gray-300">
              <div>Duration: {formatTime(exportResult.duration)}</div>
              <div>File size: {formatFileSize(exportResult.size)}</div>
              <div>Format: {exportSettings.format.toUpperCase()}</div>
            </div>
            <Button
              onClick={handleDownload}
              className="mt-3 w-full bg-green-600 hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Video
            </Button>
          </div>
        )}

        {/* Export Error */}
        {exportStatus === 'failed' && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-300 font-medium">Export failed</span>
            </div>
            <p className="text-sm text-gray-300">{exportError}</p>
            <Button
              onClick={() => setExportStatus('idle')}
              variant="outline"
              size="sm"
              className="mt-3"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Export Settings */}
        {exportStatus === 'idle' && (
          <>
            {/* File Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                File Name
              </label>
              <Input
                value={exportSettings.fileName}
                onChange={(e) => setExportSettings(prev => ({ ...prev, fileName: e.target.value }))}
                placeholder="Enter file name"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
            </div>

            {/* Platform Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Platform Presets
              </label>
              <div className="grid grid-cols-2 gap-3">
                {platformPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      exportSettings.platform === preset.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-600 hover:border-zinc-500'
                    }`}
                    onClick={() => {
                      setExportSettings(prev => ({
                        ...prev,
                        platform: preset.id,
                        quality: preset.quality,
                        format: preset.format,
                        aspectRatio: preset.aspectRatio
                      }));
                    }}
                  >
                    <div className="flex items-center mb-2">
                      <preset.icon className="w-4 h-4 mr-2 text-blue-400" />
                      <span className="font-medium text-white">{preset.name}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      <div>{preset.resolution}</div>
                      <div>{preset.aspectRatio}</div>
                      {preset.maxDuration && (
                        <div>Max: {preset.maxDuration}s</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Export Format
              </label>
              <div className="grid grid-cols-2 gap-3">
                {exportFormats.map((format) => (
                  <div
                    key={format.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      exportSettings.format === format.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-600 hover:border-zinc-500'
                    }`}
                    onClick={() => setExportSettings(prev => ({ ...prev, format: format.id }))}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-white">{format.name}</span>
                      {format.recommended && (
                        <Badge variant="outline" className="text-xs">
                          Recommended
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{format.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quality Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Quality
              </label>
              <div className="grid grid-cols-2 gap-3">
                {qualityPresets.map((quality) => (
                  <div
                    key={quality.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      exportSettings.quality === quality.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-600 hover:border-zinc-500'
                    }`}
                    onClick={() => setExportSettings(prev => ({ ...prev, quality: quality.id }))}
                  >
                    <div className="font-medium text-white mb-1">{quality.name}</div>
                    <div className="text-xs text-gray-400">
                      <div>{quality.resolution}</div>
                      <div>{quality.bitrate}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Aspect Ratio */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Aspect Ratio
              </label>
              <div className="flex space-x-3">
                <Button
                  variant={exportSettings.aspectRatio === '16:9' ? 'default' : 'outline'}
                  onClick={() => setExportSettings(prev => ({ ...prev, aspectRatio: '16:9' }))}
                  className="flex items-center space-x-2"
                >
                  <Monitor className="w-4 h-4" />
                  <span>16:9</span>
                </Button>
                <Button
                  variant={exportSettings.aspectRatio === '9:16' ? 'default' : 'outline'}
                  onClick={() => setExportSettings(prev => ({ ...prev, aspectRatio: '9:16' }))}
                  className="flex items-center space-x-2"
                >
                  <Smartphone className="w-4 h-4" />
                  <span>9:16</span>
                </Button>
              </div>
            </div>

            {/* Advanced Settings */}
            <div>
              <Button
                variant="ghost"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-gray-400 hover:text-white p-0"
              >
                <Settings className="w-4 h-4 mr-2" />
                Advanced Settings
              </Button>
              
              {showAdvanced && (
                <div className="mt-4 space-y-4 p-4 bg-zinc-800 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Frame Rate
                    </label>
                    <select
                      value={exportSettings.framerate}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, framerate: parseInt(e.target.value) }))}
                      className="w-full p-2 bg-zinc-700 border border-zinc-600 rounded text-white"
                    >
                      <option value={24}>24 FPS</option>
                      <option value={30}>30 FPS</option>
                      <option value={60}>60 FPS</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="includeAudio"
                      checked={exportSettings.includeAudio}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeAudio: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <label htmlFor="includeAudio" className="text-sm text-gray-300">
                      Include Audio
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Export Info */}
            {estimatedTime && estimatedSize && (
              <div className="bg-zinc-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-300 mb-2">Export Preview</h3>
                <div className="space-y-2 text-sm text-gray-400">
                  <div className="flex justify-between">
                    <span>Estimated time:</span>
                    <span>{formatTime(estimatedTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estimated size:</span>
                    <span>{estimatedSize.toFixed(1)} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span>{formatTime(timeline?.duration || 0)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Export Button */}
            <Button
              onClick={handleExport}
              disabled={!timeline?.clips?.length || isExporting}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Film className="w-4 h-4 mr-2" />
                  Start Export
                </>
              )}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
};

export default ExportPanel;